import { addToast } from '@heroui/react'
import Editor from '@monaco-editor/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { toast } from 'sonner'
import { $fetch } from 'utils/fetch'

const CodeEditorSection = forwardRef(({
  data,
  onCodeChange,
  onSnippetIdChange,
  onScriptCreated,
  isExpanded,
  setIsExpanded,
  setApiResponse,
  snippetVariables = {},
  testPayload,
  setTestPayload,
  hideButtons = false,
  onGenerateVariableCode,
}, ref) => {
  const queryClient = useQueryClient()
  const editorRef = useRef(null)
  const [code, setCode] = useState(data.code || `export default {
    async fetch(request, env, ctx) {
        const json = await request.json();
        // Do something with json here
        // You can extract variables defined in the section above like: json.variable_name
        // Then, return a JSON response, which can be reference as variables in your future nodes.
        return Response.json(json);
    }
}`)
  const [scriptName, setScriptName] = useState(data.scriptName || 'My_Custom_Script')
  const [selectedVersion, setSelectedVersion] = useState(null)
  const [isTestingCode, setIsTestingCode] = useState(false)
  const [testError, setTestError] = useState(null)
  const [testResponse, setTestResponse] = useState(null)
  const [localTestPayload, setLocalTestPayload] = useState(testPayload || {})
  // Add state to track code changes and original code for comparison
  const [hasCodeChanged, setHasCodeChanged] = useState(false)
  const [originalCode, setOriginalCode] = useState('')
  // Add loading state for automatic snippet creation
  const [isCreatingSnippet, setIsCreatingSnippet] = useState(false)

  // Function to insert variable extraction code
  const generateVariableExtractionCode = (variableNames) => {
    if (!editorRef.current || !variableNames || variableNames.length === 0) {
      console.error('Editor reference or variable names are missing')
      return false
    }

    const editor = editorRef.current
    const model = editor.getModel()

    if (!model) {
      console.error('Editor model is not available')
      return false
    }

    // Find the async fetch line
    const text = model.getValue()
    const lines = text.split('\n')

    let insertLineIndex = -1
    let indentation = ''

    // Find the async fetch line and the next line for insertion
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].includes('async fetch') && lines[i].includes('(')) {
        // Look for the line with the json extraction
        for (let j = i; j < Math.min(i + 10, lines.length); j++) {
          if (lines[j].includes('await request.json()')) {
            insertLineIndex = j + 2
            // Get indentation from this line
            indentation = lines[j].match(/^\s*/)[0]
            break
          }
        }
        break
      }
    }

    if (insertLineIndex === -1) {
      console.error('Could not find a good insertion point for variable extraction code')
      return false
    }

    // Generate the extraction code
    const extractionCode = variableNames.map((name) => {
      // Create a variable name with underscores instead of spaces for valid JavaScript
      const jsVarName = name.replace(/\s+/g, '_')

      // Use the original name (which might contain spaces) for the JSON property lookup
      return `${indentation}const ${jsVarName} = json["${name}"];`
    }).join('\n')

    // Add a comment
    const fullCode = `${indentation}// Extract variables from the request\n${extractionCode}\n`

    // Insert the code
    const position = { lineNumber: insertLineIndex, column: 1 }
    const range = {
      startLineNumber: insertLineIndex,
      startColumn: 1,
      endLineNumber: insertLineIndex,
      endColumn: 1,
    }

    // Insert the text
    editor.executeEdits('variable-extraction', [{
      range,
      text: fullCode,
      forceMoveMarkers: true,
    }])

    // Update the code state
    const newCode = model.getValue()
    setCode(newCode)

    // Mark code as changed
    setHasCodeChanged(true)

    // Update parent about the code change
    if (onCodeChange) {
      onCodeChange(newCode)
    }

    return true
  }

  // Expose methods to the parent component via ref
  useImperativeHandle(ref, () => ({
    executeTest: async () => {
      return executeTest()
    },
    handleSave: async () => {
      if (!data.snippet_id) {
        return createSnippetMutation.mutateAsync()
      }

      if (hasCodeChanged) {
        return updateSnippetMutation.mutateAsync()
      }

      return Promise.resolve()
    },
    // Directly expose the generateVariableExtractionCode function via ref
    generateVariableExtractionCode,
  }), [data.snippet_id, hasCodeChanged])

  // Function to handle editor mounting
  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor

    // Notify parent component that the editor is ready and code generation is available
    if (onGenerateVariableCode && typeof onGenerateVariableCode === 'function') {
      onGenerateVariableCode(generateVariableExtractionCode)
    }
  }

  // Function to sync test payload with script variables
  const handleLocalTestPayloadChange = (updatedPayload) => {
    setLocalTestPayload(updatedPayload)

    // If parent component provides a setter, use it
    if (setTestPayload) {
      setTestPayload(updatedPayload)
    }
  }

  // Update local state when props change
  useEffect(() => {
    if (testPayload && Object.keys(testPayload).length > 0) {
      setLocalTestPayload(testPayload)
    }
  }, [testPayload])

  // Update local state when snippetVariables changes
  useEffect(() => {
    // When snippetVariables change, immediately update the test payload
    if (Object.keys(snippetVariables).length > 0) {
      // Create a new test payload that takes values directly from snippetVariables
      const updatedPayload = { ...snippetVariables }

      // Update local state
      setLocalTestPayload(updatedPayload)

      // Update parent if needed
      if (setTestPayload) {
        setTestPayload(updatedPayload)
      }
    }
  }, [snippetVariables])

  // Query to get a specific snippet
  const {
    data: snippetData,
    isLoading: _isLoadingSnippet,
  } = useQuery({
    queryKey: ['snippet', data.snippet_id],
    queryFn: async () => {
      const response = await $fetch(`/v1/snippets/get/${data.snippet_id}`)
      return response.data
    },
    enabled: !!data.snippet_id,
    onSuccess: (data) => {
      if (data && data.versions && data.versions.length > 0) {
        // Get the latest version
        const latestVersion = data.versions.reduce((latest, current) => {
          return current.version > latest.version ? current : latest
        }, data.versions[0])

        setCode(latestVersion.script)
        setScriptName(data.name)
        // Store original code to track changes
        setOriginalCode(latestVersion.script)
        // Reset code changed state
        setHasCodeChanged(false)

        // Inform parent about code change
        if (onCodeChange) {
          onCodeChange(latestVersion.script)
        }
      }
    },
  })

  // Mutation to create a new snippet
  const createSnippetMutation = useMutation({
    mutationFn: async () => {
      const response = await $fetch('/v1/snippets/create', {
        method: 'POST',
        body: code,
        query: {
          language: 'js',
          name: scriptName,
        },
        headers: {
          'Content-Type': 'text/plain',
        },
      })
      return response.data
    },
    onSuccess: (data) => {
      addToast({
        title: 'Code Execution Cluster created successfully!',
      })

      // Store original code to track changes
      setOriginalCode(data.submission)
      // Reset code changed state
      setHasCodeChanged(false)

      // Update parent component with new snippet ID and code
      if (onSnippetIdChange) {
        onSnippetIdChange(data.snippet_id)
      }

      if (onCodeChange) {
        onCodeChange(data.submission)
      }

      if (onScriptCreated) {
        onScriptCreated({
          snippet_id: data.snippet_id,
          code: data.submission,
          scriptName,
        })
      }

      queryClient.invalidateQueries({ queryKey: ['snippets'] })
    },
    onError: (error) => {
      const errorMessage = error.data?.errors?.[0]?.message
      toast.error(`Failed to create script: ${errorMessage}`)
    },
    onSettled: () => {
      setIsCreatingSnippet(false)
    },
  })

  // Mutation to update an existing snippet
  const updateSnippetMutation = useMutation({
    mutationFn: async () => {
      const response = await $fetch('/v1/snippets/update', {
        method: 'POST',
        body: (typeof code === 'object' ? JSON.stringify(code) : code),
        query: {
          snippet_id: data.snippet_id,
        },
        headers: {
          'Content-Type': 'text/plain',
        },
      })
      return response.data
    },
    onSuccess: (data) => {
      toast.success('Script updated successfully!')

      // Update original code after successful update
      setOriginalCode(data.submission)
      // Reset code changed flag
      setHasCodeChanged(false)

      // Inform parent about code change
      if (onCodeChange) {
        onCodeChange(data.submission)
      }

      queryClient.invalidateQueries({ queryKey: ['snippets'] })
      queryClient.invalidateQueries({ queryKey: ['snippet', data.snippet_id] })
    },
    onError: (error) => {
      const errorMessage = error.data?.errors?.[0]?.message
      toast.error(`Failed to update script: ${errorMessage}`)
    },
  })

  // Mutation to run the script
  const runScriptMutation = useMutation({
    mutationFn: async () => {
      // Make sure we're using the most up-to-date payload
      const currentPayload = { ...snippetVariables }

      const response = await $fetch(`/v1/snippets/run/${data.snippet_id}`, {
        method: 'POST',
        body: {
          payload: currentPayload,
          headers: {},
          version: selectedVersion,
        },
      })
      return response.data
    },
    onSuccess: (data) => {
      setApiResponse(data)
      setTestResponse(data)
      setTestError(null)
      toast.success('Script executed successfully!')
    },
    onError: (error) => {
      const errorMessage = error.data?.errors?.[0]?.message
      setApiResponse(null)
      setTestError(errorMessage || 'Failed to execute script')
      setTestResponse(null)
      toast.error(`Failed to execute script: ${errorMessage}`)
    },
    onSettled: () => {
      setIsTestingCode(false)
    },
  })

  // Automatically create a snippet if one doesn't exist yet
  useEffect(() => {
    const autoCreateSnippet = async () => {
      if (!data.snippet_id && !isCreatingSnippet && code) {
        setIsCreatingSnippet(true)
        try {
          await createSnippetMutation.mutateAsync()
        }
        catch (error) {
          console.error('Error auto-creating snippet:', error)
        }
      }
    }

    autoCreateSnippet()
  }, [data.snippet_id, code])

  // Handle save function
  const handleSave = async () => {
    // If there's no snippet_id, create one
    if (!data.snippet_id) {
      return createSnippetMutation.mutate()
    }

    // Only update if code has changed
    if (hasCodeChanged) {
      return updateSnippetMutation.mutate()
    }
    else {
      toast.info('No changes to save')
      return Promise.resolve()
    }
  }

  // Handle test function
  const handleTest = async () => {
    // If no snippet_id, we need to create one first
    if (!data.snippet_id) {
      toast.info('Creating script before testing...')
      try {
        await createSnippetMutation.mutateAsync()
        // After creation, we can run the test
        setIsTestingCode(true)
        runScriptMutation.mutate()
      }
      catch (error) {
        toast.error('Failed to create script before testing')
      }
      return
    }

    // If code has changed, update it first
    if (hasCodeChanged) {
      toast.info('Updating script before testing...')
      try {
        await updateSnippetMutation.mutateAsync()
        // After update, run the test
        setIsTestingCode(true)
        runScriptMutation.mutate()
      }
      catch (error) {
        toast.error('Failed to update script before testing')
      }
      return
    }

    // If no changes, just run the test
    setIsTestingCode(true)
    runScriptMutation.mutate()
  }

  // Expose public methods for parent component
  // This function executes the testing workflow
  const executeTest = async () => {
    return new Promise((resolve, reject) => {
      // Use the most current snippetVariables from props for testing
      const currentTestPayload = { ...snippetVariables }

      // Update local state to ensure consistency
      setLocalTestPayload(currentTestPayload)

      // If snippet_id doesn't exist, create it first
      if (!data.snippet_id) {
        createSnippetMutation.mutate(null, {
          onSuccess: async () => {
            // After creation, proceed with testing
            setIsTestingCode(true)
            runScriptMutation.mutate(null, {
              onSuccess: data => resolve(data),
              onError: error => reject(error),
            })
          },
          onError: error => reject(error),
        })
        return
      }

      // If code has changed, update it first
      if (hasCodeChanged) {
        updateSnippetMutation.mutate(null, {
          onSuccess: () => {
            // After update, proceed with testing
            setIsTestingCode(true)
            runScriptMutation.mutate(null, {
              onSuccess: data => resolve(data),
              onError: error => reject(error),
            })
          },
          onError: error => reject(error),
        })
        return
      }

      // If no changes needed, just run the test
      setIsTestingCode(true)
      runScriptMutation.mutate(null, {
        onSuccess: data => resolve(data),
        onError: error => reject(error),
      })
    })
  }

  // Update local state when data changes
  useEffect(() => {
    if (data.code) {
      setCode(data.code)
    }
    if (data.scriptName) {
      setScriptName(data.scriptName)
    }
  }, [data])

  return (
    <div className="flex flex-col gap-y-6">
      <div className="flex flex-col gap-y-4">
        {/* Code Editor */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            {!hideButtons && (
              <div className="flex gap-3">
                <button
                  onClick={handleSave}
                  className={`
                    bg-indigo-500 px-3 py-1 text-xs text-white
                    hover:bg-indigo-600
                  `}
                  disabled={
                    createSnippetMutation.isPending
                    || updateSnippetMutation.isPending
                    || (!data.snippet_id && isCreatingSnippet)
                    || (data.snippet_id && !hasCodeChanged)
                  }
                >
                  {data.snippet_id ? 'Update Script' : 'Save Script'}
                  {hasCodeChanged && data.snippet_id && ' *'}
                </button>
                <button
                  onClick={handleTest}
                  className={`
                    bg-emerald-500 px-3 py-1 text-xs text-white
                    hover:bg-emerald-600
                  `}
                  disabled={
                    isTestingCode
                    || runScriptMutation.isPending
                    || createSnippetMutation.isPending
                    || updateSnippetMutation.isPending
                    || isCreatingSnippet
                  }
                >
                  {isTestingCode ? 'Testing...' : 'Test Script'}
                </button>
              </div>
            )}
          </div>
          <Editor
            height="300px"
            width="100%"
            language="javascript"
            value={code}
            onChange={(value) => {
              const newCode = value || ''
              setCode(newCode)
              // Check if code has changed from the original
              setHasCodeChanged(newCode !== originalCode)

              // Inform parent of code changes
              if (onCodeChange) {
                onCodeChange(newCode)
              }
            }}
            options={{
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              lineNumbers: false,
              fontSize: 14,
              wordWrap: 'on',
            }}
            onMount={handleEditorDidMount}
            className="w-full overflow-hidden border border-gray-200"
          />
        </div>
      </div>
    </div>
  )
})

// Update the static method to use the proper ref format
CodeEditorSection.executeTest = async function (ref) {
  if (ref && ref.current && ref.current.executeTest) {
    return ref.current.executeTest()
  }
  return Promise.reject(new Error('Test execution not available'))
}

export default CodeEditorSection
